import React from 'react';
import { Container, Table, Typography } from 'amphitheatre';

import Pic from './pic.png';

const { Header, Paragraph } = Typography;

const columns = [
  {
    id: 'item',
    text: 'ITEM',
  },
  {
    id: 'type',
    text: 'TYPE',
  },
  {
    id: 'power',
    text: 'POWER',
  },
  {
    id: 'cost',
    text: 'COST',
  },
];

const data = [
  { id: 1, item: 'CubeSat', type: '1U', power: '1W', cost: '$150k' },
  { id: 2, item: '', type: '2U', power: '2W', cost: '$250k' },
  { id: 3, item: '', type: '3U', power: '3W', cost: '$350k' },
  { id: 4, item: '', type: '6U', power: '12W', cost: '$750k' },
  { id: 5, item: 'Microsat', type: '50kg', power: '250W', cost: '$1.5m' },
  {
    id: 6,
    item: 'Ground Station',
    type: 'Dedicated - optimised for high rate data',
    power: '',
    cost: '$400k',
  },
  {
    id: 7,
    item: '',
    type: 'Dedicated - optimised for high rate data',
    power: '',
    cost: '$150k',
  },
  {
    id: 8,
    item: '',
    type: 'Outsourced supplier, LEO',
    power: '',
    cost: '$12/min',
  },
  {
    id: 9,
    item: '',
    type: 'Outsourced supplier, GEO',
    power: '',
    cost: '$20/min',
  },
  {
    id: 10,
    item: 'Operations',
    type: 'Dedicated running your own MCC',
    power: '',
    cost: '$200k-$600k/year',
  },
  {
    id: 11,
    item: '',
    type: 'Outsourced supplier',
    power: '',
    cost: '$2k/month/satellite',
  },
];

const Template: React.FunctionComponent = () => {
  return (
    <Container className="content-container" size="lg">
      <Header>Quest For Blue Business Case Template </Header>
      {/*
        **************************
        Welcome to Quest For Blue!
        **************************
      */}
      <Header level={5} style={{ fontWeight: 'bold' }}>
        Welcome to Quest For Blue!
      </Header>
      <Paragraph>
        This document overviews a simple template to guide you in the writeup
        portion of your selection. An example is provided to help you and we are
        available on the
        <a href="https://www.facebook.com/groups/questforblue">
          Facebook page.
        </a>
      </Paragraph>
      <Paragraph>
        Spacecraft engineering, mission design, and business planning are all
        highly complex and interrelated tasks. Normally a full concurrent design
        would be needed to close the loop. In order to reduce complexity many
        aspects of a normal design process are abstracted—spacecraft design,
        launch, etc only require a ‘reasonable assumption’ to justify selection.
        This allows both the competitors (you!) and the reviewers (that’s us!)
        to focus on the end product mission plan. Rough costs are provided for
        spacecraft purchases that are allowable.
      </Paragraph>
      <Paragraph>
        Note in all cases the term “reasonable” and “approximate” has a lot of
        latitude and is subjective, provided to add some guidelines and
        flexibility, but must still be based on available technology. In all
        cases, Saber Astronautics judges will disagree if the author decides to
        use unobtanium in their submissions.
      </Paragraph>
      <Paragraph>
        <i>
          <b>
            <span style={{ color: 'red' }}>DO NOT</span>
          </b>{' '}
          submit link budgets or spacecraft schematics in your submissions.
        </i>
      </Paragraph>
      <Paragraph>
        <i>
          <b>
            <span style={{ color: 'green' }}>DO</span>
          </b>{' '}
          submit your spreadsheet calcs and PIGI output files for your{' '}
          <b>
            <span style={{ color: 'green' }}>FINAL</span>
          </b>{' '}
          orbits.
        </i>
      </Paragraph>
      {/*
        ********************
        Customer and Product
        ********************
      */}
      <Header level={5} style={{ fontWeight: 'bold' }}>
        Customer and Product
      </Header>
      <Paragraph>
        Describe the customer. We want to know where the customer is located,
        what their needs are, and how they will get their product. Define your
        product. We also want to know:
      </Paragraph>
      <Paragraph>
        <ol>
          <li>
            The type and quality of the product that your customer needs. For
            example, if your customer needs a satellite photo tell us the per
            meter resolution and how they will use the image.
          </li>
          <li>
            The frequency of product delivery. What is the acceptable gap time?
          </li>
          <li>
            The approximate size of the product, in bits, or other
            characteristics related to product frequency.
          </li>
          <li>How much will you charge per product and why?</li>
          <li>Why does your customer need this product?</li>
        </ol>
      </Paragraph>
      {/*
        *******
        Payload
        *******
      */}
      <Header level={5} style={{ fontWeight: 'bold' }}>
        Payload
      </Header>
      <Paragraph>
        Describe a payload that is available today that can produce products at
        the rough rate and quality needed to support your customer from above.
        This section will present a commercially available (or academically
        available) payload which is at TRL-6 or better. If the product is not
        data related use this section to describe how the product is delivered
        to the customer and how the product affects the spacecraft selection.
      </Paragraph>
      <Paragraph>What we want to know:</Paragraph>
      <Paragraph>
        <ol>
          <li>
            A brief physical description of the payload, size/mass, cost, and
            anything else that may affect the mission requirements.
          </li>
          <li>
            Evidence that the quality of the product produced by the payload
            fits the customer’s needs.
          </li>
          <li>
            Any orbital constraints the payload may have. For example, a low
            fidelity camera can produce a reasonable image for a customer at
            LEO, but lose fidelity with altitude.
          </li>
        </ol>
      </Paragraph>
      {/*
        ******************************
        Spacecraft Selection
        ******************************
      */}
      <Header level={5} style={{ fontWeight: 'bold' }}>
        Spacecraft Selection
      </Header>
      <Paragraph>
        For the sake of simplicity Quest For Blue{' '}
        <b>
          <span style={{ color: 'red' }}>
            does not require a spacecraft design
          </span>
        </b>{' '}
        and is abstractly provided. Instead there are several pre-selected
        options for you to choose from. The following options are provided as a
        guideline and can be deviated if there are reasonable assumptions
        provided in this section. You can use your own concept as well provided
        the assumptions are justified in your description. You must justify that
        your spacecraft can support the payload.
      </Paragraph>
      <Paragraph>What we want to know</Paragraph>
      <Paragraph>
        <ol>
          <li>
            An overview of the design considerations. Include any special
            requirements for any spacecraft
          </li>
          <li>
            The transmitter selection, frequency, and bandwidth (this is needed
            for downlink rates below)
          </li>
          <li>Reasonable fit for the payload selection</li>
        </ol>
      </Paragraph>
      {/*
        ******************************
        Budgeted Costs
        ******************************
      */}
      <Header level={5} style={{ fontWeight: 'bold' }}>
        Budgeted Costs
      </Header>
      <Paragraph>
        You have $10m to develop your mission. This investment may be used to
        purchase any number of spacecraft, ground stations, and other mission
        needs in order to support your mission. Satellite cost assume that the
        launch is included as a rideshare.
      </Paragraph>
      <Paragraph>
        The following numbers can be used for cost estimation. You may modify
        these cost estimates if reasonable justification in your writeup. Or you
        may come up with your own category if, again, you can reasonably justify
        your assumptions based on existing technology and processes.
      </Paragraph>
      <Paragraph>
        <Table
          style={{
            borderRadius: '15px',
            boxShadow: '5px 5px 10px #050505',
          }}
          columns={columns}
          data={data}
        />
      </Paragraph>
      {/*
        ******************************
        Mission Orbit and Total Addressable Market (TAM) Calculations
        ******************************
      */}
      <Header level={5} style={{ fontWeight: 'bold' }}>
        Mission Orbit and
        <a href="https://en.wikipedia.org/wiki/Total_addressable_market">
          Total Addressable Market (TAM) Calculations
        </a>
      </Header>
      <Paragraph>
        You should use PIGI to create your satellites for your orbits and ground
        stations. Orbits must reflect reasonable constraints from your payload,
        transmitter, and ground station selection. Note that the altitude upper
        limit depends on the transmitter you select and the ground station.
      </Paragraph>
      <Paragraph>To calculate the overpass for your mission</Paragraph>
      <Paragraph>
        <ol>
          <li>
            From your PIGI time-bar, click on the “show overpass info” button.
            This will bring up the OverPass information dialog.
          </li>
          <li>
            By default you should have all of the ground stations and satellites
            available in your current scene. Ensure “Use Julian Date” is
            checked. Click the “Calculate” button.
          </li>
          <li>
            Export to .csv. Files are saved in the default folder unless you
            change the path. For Windows it is here: C:\Program Files\Saber
            Astronautics\PIGI\PIGI\PIGI_Data
          </li>
        </ol>
      </Paragraph>
      <Paragraph>
        <img
          src={Pic}
          alt="Overpass export tutorial"
          style={{ width: '100%' }}
        />
      </Paragraph>
      <Paragraph>
        The .csv spreadsheet with overpass durations is the baseline you will
        need to estimate the value of your mission. Converting overpass
        durations to an estimate requires the following steps:
      </Paragraph>
      <Paragraph>
        <ol>
          <li>
            Change the duration to a readable form: Subtract the LOS from AOS.
          </li>
          <li>
            Sum up all of the durations. This is your daily overpass for your
            constellation. Consider also how your gap times compare with your
            customer’s expected experiences.
          </li>
          <li>
            Add a tab in your spreadsheet to calculate the number of products
            based on downlink rates (spacecraft selection section) using the
            example in QFB_Example.csv.
          </li>
        </ol>
      </Paragraph>
      <Paragraph>
        The important result is the dollar rate per day which is producible by
        your system. It is normal to adjust your mission parameters (orbits,
        phasing angles, etc) and other factors based on the revenue calculation,
        re-enter new values, and re-calculate your rate.
      </Paragraph>
      <Paragraph>
        Given the addressable market from this section and the costs estimates
        from the Budgeted Costs section above, you can now explain the viability
        of your space business.
      </Paragraph>
    </Container>
  );
};

export default Template;
